import Logger from './logger';

// Crypto utility - works in browser and Jest test environments
const getCrypto = (): Crypto => {
  // Browser environment - Web Crypto API
  if (typeof window !== 'undefined' && window.crypto) {
    return window.crypto;
  }
  
  // Jest/test environment fallback
  // Mock crypto API for tests (not cryptographically secure, but works for tests)
  const mockCrypto = {
    getRandomValues: (arr: Uint8Array): Uint8Array => {
      for (let i = 0; i < arr.length; i++) {
        arr[i] = Math.floor(Math.random() * 256);
      }
      return arr;
    },
    subtle: {
      importKey: async () => ({} as CryptoKey),
      encrypt: async () => new ArrayBuffer(0),
      decrypt: async () => new ArrayBuffer(0),
    } as unknown as SubtleCrypto
  };
  
  return mockCrypto as Crypto;
};

const crypto = getCrypto();

// Encryption configuration
interface EncryptionConfig {
  algorithm: string;
  keyLength: number;
  ivLength: number;
  tagLength: number;
}

// Encrypted data structure
interface EncryptedData {
  data: string;
  iv: string;
  tag: string;
  algorithm: string;
  timestamp: number;
}

// Key management interface
interface EncryptionKey {
  id: string;
  key: string;
  algorithm: string;
  createdAt: number;
  isActive: boolean;
  usageCount: number;
}

// Data classification levels
export enum DataClassification {
  PUBLIC = 'public',
  INTERNAL = 'internal',
  CONFIDENTIAL = 'confidential',
  RESTRICTED = 'restricted'
}

// Field encryption metadata
interface FieldEncryptionMetadata {
  fieldName: string;
  classification: DataClassification;
  isEncrypted: boolean;
  encryptionKeyId: string;
  encryptedAt: number;
}

class DataEncryptionService {
  private config: EncryptionConfig;
  private keys: Map<string, EncryptionKey>;
  private currentKeyId: string;
  private isInitialized: boolean = false;

  constructor() {
    this.config = {
      algorithm: 'AES-GCM',
      keyLength: 256,
      ivLength: 12,
      tagLength: 16
    };
    this.keys = new Map();
    this.currentKeyId = '';
    this.initialize();
  }

  private async initialize(): Promise<void> {
    try {
      // Generate or retrieve encryption key
      await this.initializeEncryptionKey();
      this.isInitialized = true;
      Logger.info('Data encryption service initialized successfully');
    } catch (error) {
      Logger.error('Failed to initialize encryption service', error instanceof Error ? error : new Error('Unknown error'));
      this.isInitialized = false;
    }
  }

  private async initializeEncryptionKey(): Promise<void> {
    try {
      // Check if we have a stored key
      const storedKey = localStorage.getItem('encryption_key');
      
      if (storedKey) {
        const keyData = JSON.parse(storedKey);
        this.currentKeyId = keyData.id;
        this.keys.set(keyData.id, keyData);
        Logger.info('Loaded existing encryption key');
      } else {
        // Generate new key
        await this.generateNewKey();
        Logger.info('Generated new encryption key');
      }
    } catch (error) {
      Logger.error('Failed to initialize encryption key', error instanceof Error ? error : new Error('Unknown error'));
      throw error;
    }
  }

  private async generateNewKey(): Promise<void> {
    try {
      const keyId = `key_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const key = await this.generateRandomKey();
      
      const encryptionKey: EncryptionKey = {
        id: keyId,
        key: key,
        algorithm: this.config.algorithm,
        createdAt: Date.now(),
        isActive: true,
        usageCount: 0
      };

      this.keys.set(keyId, encryptionKey);
      this.currentKeyId = keyId;

      // Store key securely
      localStorage.setItem('encryption_key', JSON.stringify(encryptionKey));
    } catch (error) {
      Logger.error('Failed to generate encryption key', error instanceof Error ? error : new Error('Unknown error'));
      throw error;
    }
  }

  private async generateRandomKey(): Promise<string> {
    const array = new Uint8Array(this.config.keyLength / 8);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  }

  private async generateIV(): Promise<string> {
    const array = new Uint8Array(this.config.ivLength);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  }

  private async encryptData(data: string, key: string): Promise<EncryptedData> {
    try {
      const iv = await this.generateIV();
      const keyBuffer = new Uint8Array(key.match(/.{1,2}/g)!.map(byte => parseInt(byte, 16)));
      const ivBuffer = new Uint8Array(iv.match(/.{1,2}/g)!.map(byte => parseInt(byte, 16)));
      const dataBuffer = new TextEncoder().encode(data);

      const cryptoKey = await crypto.subtle.importKey(
        'raw',
        keyBuffer,
        { name: this.config.algorithm },
        false,
        ['encrypt']
      );

      const encrypted = await crypto.subtle.encrypt(
        { name: this.config.algorithm, iv: ivBuffer },
        cryptoKey,
        dataBuffer
      );

      const encryptedArray = new Uint8Array(encrypted);
      const encryptedHex = Array.from(encryptedArray, byte => byte.toString(16).padStart(2, '0')).join('');

      return {
        data: encryptedHex,
        iv: iv,
        tag: '', // GCM includes tag in encrypted data
        algorithm: this.config.algorithm,
        timestamp: Date.now()
      };
    } catch (error) {
      Logger.error('Encryption failed', error instanceof Error ? error : new Error('Unknown error'));
      throw error;
    }
  }

  private async decryptData(encryptedData: EncryptedData, key: string): Promise<string> {
    try {
      const keyBuffer = new Uint8Array(key.match(/.{1,2}/g)!.map(byte => parseInt(byte, 16)));
      const ivBuffer = new Uint8Array(encryptedData.iv.match(/.{1,2}/g)!.map(byte => parseInt(byte, 16)));
      const dataBuffer = new Uint8Array(encryptedData.data.match(/.{1,2}/g)!.map(byte => parseInt(byte, 16)));

      const cryptoKey = await crypto.subtle.importKey(
        'raw',
        keyBuffer,
        { name: this.config.algorithm },
        false,
        ['decrypt']
      );

      const decrypted = await crypto.subtle.decrypt(
        { name: this.config.algorithm, iv: ivBuffer },
        cryptoKey,
        dataBuffer
      );

      return new TextDecoder().decode(decrypted);
    } catch (error) {
      Logger.error('Decryption failed', error instanceof Error ? error : new Error('Unknown error'));
      throw error;
    }
  }

  // Public API methods
  async encryptField(value: string, classification: DataClassification = DataClassification.CONFIDENTIAL): Promise<EncryptedData> {
    if (!this.isInitialized) {
      throw new Error('Encryption service not initialized');
    }

    if (!value || typeof value !== 'string') {
      return {
        data: value,
        iv: '',
        tag: '',
        algorithm: 'none',
        timestamp: Date.now()
      };
    }

    try {
      const key = this.keys.get(this.currentKeyId);
      if (!key) {
        throw new Error('No encryption key available');
      }

      const encrypted = await this.encryptData(value, key.key);
      
      // Update key usage
      key.usageCount++;
      this.keys.set(key.id, key);

      return encrypted;
    } catch (error) {
      Logger.error('Field encryption failed', error instanceof Error ? error : new Error('Unknown error'));
      throw error;
    }
  }

  async decryptField(encryptedData: EncryptedData): Promise<string> {
    if (!this.isInitialized) {
      throw new Error('Encryption service not initialized');
    }

    if (encryptedData.algorithm === 'none') {
      return encryptedData.data;
    }

    try {
      const key = this.keys.get(this.currentKeyId);
      if (!key) {
        throw new Error('No encryption key available');
      }

      return await this.decryptData(encryptedData, key.key);
    } catch (error) {
      Logger.error('Field decryption failed', error instanceof Error ? error : new Error('Unknown error'));
      throw error;
    }
  }

  async encryptDocument(document: any, fieldsToEncrypt: string[]): Promise<any> {
    if (!this.isInitialized) {
      throw new Error('Encryption service not initialized');
    }

    const encryptedDoc = { ...document };
    const encryptionMetadata: FieldEncryptionMetadata[] = [];

    for (const fieldName of fieldsToEncrypt) {
      if (document[fieldName] && typeof document[fieldName] === 'string') {
        try {
          const encrypted = await this.encryptField(document[fieldName]);
          encryptedDoc[fieldName] = encrypted;
          
          encryptionMetadata.push({
            fieldName,
            classification: DataClassification.CONFIDENTIAL,
            isEncrypted: true,
            encryptionKeyId: this.currentKeyId,
            encryptedAt: Date.now()
          });
        } catch (error) {
          Logger.error(`Failed to encrypt field ${fieldName}`, error instanceof Error ? error : new Error('Unknown error'));
          // Keep original value if encryption fails
        }
      }
    }

    // Add encryption metadata
    encryptedDoc._encryptionMetadata = encryptionMetadata;

    return encryptedDoc;
  }

  async decryptDocument(document: any): Promise<any> {
    if (!this.isInitialized) {
      throw new Error('Encryption service not initialized');
    }

    const decryptedDoc = { ...document };
    const encryptionMetadata = document._encryptionMetadata || [];

    for (const metadata of encryptionMetadata) {
      if (metadata.isEncrypted && document[metadata.fieldName]) {
        try {
          const decrypted = await this.decryptField(document[metadata.fieldName]);
          decryptedDoc[metadata.fieldName] = decrypted;
        } catch (error) {
          Logger.error(`Failed to decrypt field ${metadata.fieldName}`, error instanceof Error ? error : new Error('Unknown error'));
          // Keep encrypted value if decryption fails
        }
      }
    }

    // Remove encryption metadata
    delete decryptedDoc._encryptionMetadata;

    return decryptedDoc;
  }

  getEncryptionStatus(): { isInitialized: boolean; keyCount: number; currentKeyId: string } {
    return {
      isInitialized: this.isInitialized,
      keyCount: this.keys.size,
      currentKeyId: this.currentKeyId
    };
  }

  async rotateKey(): Promise<void> {
    try {
      // Generate new key
      await this.generateNewKey();
      Logger.info('Encryption key rotated successfully');
    } catch (error) {
      Logger.error('Failed to rotate encryption key', error instanceof Error ? error : new Error('Unknown error'));
      throw error;
    }
  }

  // Data classification helper
  classifyField(fieldName: string, value: any): DataClassification {
    const sensitiveFields = ['email', 'phone', 'ssn', 'creditCard', 'bankAccount', 'password', 'token'];
    const confidentialFields = ['address', 'name', 'company', 'rate', 'price', 'cost'];
    
    if (sensitiveFields.some(field => fieldName.toLowerCase().includes(field))) {
      return DataClassification.RESTRICTED;
    }
    
    if (confidentialFields.some(field => fieldName.toLowerCase().includes(field))) {
      return DataClassification.CONFIDENTIAL;
    }
    
    if (fieldName.toLowerCase().includes('public') || fieldName.toLowerCase().includes('status')) {
      return DataClassification.PUBLIC;
    }
    
    return DataClassification.INTERNAL;
  }
}

// Export singleton instance
const dataEncryptionService = new DataEncryptionService();
export default dataEncryptionService;
export { type EncryptedData, type FieldEncryptionMetadata };
